'use client';
import { useEffect, useLayoutEffect } from 'react';
import { appConfig } from '../../utils/appConfig';
import MobileNav, { MobileNavProps } from './_MobileNav';
import NavDropdownWrapper from './_NavDropdownWrapper';
import Search from './_GlobalSearch';
import SitewideBanner, { type SitewideBannerProps } from './_SitewideBanner';
import Button from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import HamburgerIcon from '@repo/ui/src/components/Icons/HamburgerIcon';
import HeaderPaddingWrapper from '@repo/ui/src/components/HeaderPaddingWrapper';
import { useScrollLock } from '@repo/ui/src/hooks/useScrollLock';
import FocusTrap from 'focus-trap-react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import React, { useState } from 'react';
import { LinkProps } from '@repo/ui/src/types/link';
import { MetaProps } from '@repo/ui/src/types';
import SecondaryNav, { SecondaryNavProps } from './_SecondaryNav';
import NavDropdown, { NavDropdownProps } from './_NavDropdown';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import SearchIcon from '@repo/ui/src/components/Icons/SearchIcon';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { Region } from '@repo/ui/src/types/LanguageSelector';
import { classNames } from '@repo/ui/src/utils/classNames';

export interface NavItemProps {
  _meta: MetaProps;
  title: string;
  titleAsLink?: LinkProps;
  content: NavDropdownProps | SecondaryNavProps;
}

export interface NavigationProps {
  searchLink: LinkProps;
  navItems: Array<NavItemProps>;
  sitewideBanner: SitewideBannerProps;
  regions: Array<Region>;
  NavbarBrand?: React.ComponentType;
  NavbarShoppingCart?: React.ComponentType;
  UserAvatar?: React.ComponentType<{
    className?: string;
    loggedInLabel?: string;
    loggedOutText?: string;
  }>;
  SocialLinks?: React.ComponentType<{ socialLinks: any }>;
  account?: MobileNavProps['account'];
  useKeyboardNavigation?: (options: any) => (e: React.KeyboardEvent) => void;
  useOnRouteChange?: (callback: () => void) => void;
  useCustomer?: () => { data: any };
  useLogout?: () => { trigger: () => void };
  getTZDate?: (dateString: string) => Date;
}

const componentTypes = {
  'secondaryNav.json': SecondaryNav,
  'navDropdown.json': NavDropdown,
};

export const isDropdown = (item: NavItemProps) =>
  item.content?._meta?.schema?.endsWith('navDropdown.json');

const NavigationAnimationContext = React.createContext({
  setHeight: noop,
  setOffset: noop,
});

export const useNavigationContext = () => React.useContext(NavigationAnimationContext);

const Navigation = ({
  navItems,
  searchLink,
  sitewideBanner,
  regions,
  NavbarBrand,
  NavbarShoppingCart,
  UserAvatar,
  SocialLinks,
  account,
  useKeyboardNavigation = () => noop,
  useOnRouteChange = noop,
  useCustomer,
  useLogout,
  getTZDate,
}: NavigationProps) => {
  const { lock, unlock } = useScrollLock();
  const sitewideBannerRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLElement>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const [dropdownOffset, setDropdownOffset] = useState(0);

  useLayoutEffect(() => {
    const getOffsetHeight = () => {
      const headerHeight = headerRef.current?.getBoundingClientRect().height ?? 0;
      const bannerHeight = sitewideBannerRef.current?.getBoundingClientRect().height ?? 0;
      setOffset(headerHeight + bannerHeight);
    };

    getOffsetHeight();

    // In case dynamic content loads later
    const timeout = setTimeout(getOffsetHeight, 200);
    const handleResize = debounce(getOffsetHeight, 200);

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (openDropdown != null) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && openDropdown != null) {
          (
            headerRef.current?.querySelector(
              `[data-testid="main-nav-${openDropdown}"]`,
            ) as HTMLElement
          )?.focus();
          setOpenDropdown(null);
        }
      };

      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [openDropdown]);

  useOnRouteChange(() => {
    setMobileDrawerOpen(false);
    setOpenDropdown(null);
  });

  useEffect(() => {
    if (mobileDrawerOpen || openDropdown != null) {
      lock();
    } else {
      unlock();
    }
  }, [mobileDrawerOpen, openDropdown, lock, unlock]);

  const handleKeyDown: (e: React.KeyboardEvent) => void = useKeyboardNavigation({
    items: navItems,
    onChange: (index: number) => {
      setOpenDropdown(index);
      (
        headerRef.current?.querySelector(`[data-testid="main-nav-${index}"]`) as HTMLElement
      )?.focus();
    },
  });

  if (!navItems || navItems.length === 0) return null;

  return (
    <>
      <Button
        as={Anchor}
        link="#main-content"
        size="small"
        className={classNames(
          'tw-z-[1100] tw-absolute -tw-top-20 tw-transition-top tw-ease-in-out tw-duration-[250] focus:tw-top-1 tw-left-1',
        )}
      >
        {'Skip to Main Content'}
      </Button>
      <div ref={sitewideBannerRef}>
        {sitewideBanner && (
          <SitewideBanner {...sitewideBanner} regions={regions} getTZDate={getTZDate} />
        )}
      </div>
      <FocusTrap active={mobileDrawerOpen}>
        <header
          className={classNames(
            'tw-sticky tw-top-0 tw-z-[1001] tw-block',
            openDropdown != null &&
            'after:tw-opacity-50 after:tw-bg-black after:tw-fixed after:tw-inset-0 after:tw-transition-opacity after:-tw-z-30',
          )}
          ref={headerRef}
          data-testid="overlay-header"
        >
          <NavigationAnimationContext.Provider
            value={{ setHeight: setDropdownHeight, setOffset: setDropdownOffset }}
          >
            <div
              onMouseLeave={() => setOpenDropdown(null)}
              className={classNames(
                'tw-bg-neutral-700 !tw-text-white tw-flex tw-flex-col',
                'after:tw-inset-x-0 after:tw-top-full after:-tw-z-20 after:tw-absolute after:tw-block after:tw-bg-warm-100 after:tw-h-[calc(var(--dropdown-height,0)+var(--dropdown-offset,0))] after:tw-max-h-[calc(85vh-var(--tra-nav-height))] after:[transition:height_500ms,opacity_300ms] after:tw-duration-500',
                openDropdown === null
                  ? 'after:tw-opacity-0 after:[transition:height_500ms_500ms,opacity_300ms]'
                  : 'after:tw-opacity-100',
              )}
              aria-label="main"
              style={{
                // @ts-expect-error unknown style
                '--dropdown-height': `${dropdownHeight}px`,
                '--dropdown-offset': `${dropdownOffset}px`,
              }}
            >
              <HeaderPaddingWrapper
                className="tw-min-h-navHeight tw-w-full tw-max-h-[500px] tw-items-center tw-grid tw-grid-flow-col tw-justify-between md:tw-grid-cols-[1fr_auto_1fr] tw-gap-5 tw-self-start"
                data-testid="desktop-nav"
                as="nav"
                aria-label="desktop"
              >
                {NavbarBrand && <NavbarBrand />}
                <ul className="tw-hidden tw-list-none sm:tw-flex tw-justify-center tw-items-center tw-gap-x-10 tw-pl-0">
                  {navItems.map((item, i) => {
                    const hasContent = !!item.content;
                    const hasLink = !!item.titleAsLink;

                    if (!hasContent && !hasLink) return null;

                    const isOpen = openDropdown === i;
                    return (
                      <li
                        key={i}
                        onMouseEnter={() =>
                          hasContent ? setOpenDropdown(i) : setOpenDropdown(null)
                        }
                      >
                        {item.titleAsLink && !hasContent ? (
                          <Anchor
                            {...item.titleAsLink}
                            data-testid={`main-nav-${i}`}
                            onClick={() => {
                              navigationClicked({
                                nav_type: 'Top Navigation',
                                clicked_text: navItems[i].title ?? '',
                                primary_header: navItems[i].title ?? '',
                              });
                            }}
                            className={classNames(
                              'tra-text-web-sub-2 tw-relative after:tw-transition-all after:tw-duration-300 after:tw-opacity-100 after:tw-absolute after:tw-inset-x-0 after:-tw-bottom-2 after:tw-w-full after:tw-h-[4px] after:tw-bg-brand-300 after:tw-scale-0 after:tw-origin-center hover:after:tw-scale-100',
                              appConfig.isMeater &&
                              'tra-text-web-utl-button-m after:tw-h-[2px] after:-tw-bottom-3',
                            )}
                          >
                            {item.title}
                          </Anchor>
                        ) : (
                          <button
                            data-testid={`main-nav-${i}`}
                            data-index={i}
                            onKeyDown={handleKeyDown}
                            onFocus={() => setOpenDropdown(i)}
                            onClick={() => {
                              navigationClicked({
                                nav_type: 'Top Navigation',
                                clicked_text: navItems[i].title ?? '',
                                primary_header: navItems[i].title ?? '',
                              });
                            }}
                            className={classNames(
                              'tra-text-web-sub-2 tw-relative after:tw-transition-all after:tw-duration-300 after:tw-opacity-100 after:tw-absolute after:tw-inset-x-0 after:-tw-bottom-2 after:tw-w-full after:tw-h-[4px] after:tw-bg-brand-300 after:tw-scale-0 after:tw-origin-center hover:after:tw-scale-100',
                              isOpen && 'after:tw-scale-100',
                              appConfig.isMeater &&
                              'tra-text-web-utl-button-m after:tw-h-[2px] after:-tw-bottom-3',
                            )}
                            aria-expanded={isOpen}
                            aria-controls={`nav-dropdown-${i}`}
                            aria-haspopup="true"
                          >
                            {item.title}
                          </button>
                        )}
                        {hasContent && (
                          <NavDropdownWrapper
                            id={`nav-dropdown-${i}`}
                            open={isOpen}
                            isDropdown={isDropdown(item)}
                            onTransitionEnd={() => {
                              if (openDropdown === null) {
                                setDropdownHeight(0);
                                setDropdownOffset(0);
                              }
                            }}
                          >
                            <DynamicComponent
                              {...item.content}
                              _componentTypes={componentTypes}
                              searchLink={searchLink}
                              open={openDropdown === i && hasContent}
                              primaryHeader={item?.title}
                              SocialLinks={SocialLinks}
                            />
                          </NavDropdownWrapper>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <ul className="tw-list-none tw-pl-0 tw-flex tw-flex-row tw-items-center tw-justify-end md:tw-gap-4 -tw-mr-3 sm:-tw-mr-1">
                  {searchLink?.link && (
                    <>
                      <Anchor
                        className="tw-p-3 lg:tw-hidden"
                        aria-label="Search"
                        link={searchLink.link}
                      >
                        <SearchIcon className="tw-w-6" />
                      </Anchor>
                      <li>
                        <Search placeholder={searchLink.displayName} link={searchLink.link} />
                      </li>
                    </>
                  )}
                  {UserAvatar && (
                    <li>
                      <UserAvatar
                        className="tw-hidden md:tw-inline-block"
                        loggedInLabel={account?.loggedInLabel}
                        loggedOutText={account?.loggedOutText}
                      />
                    </li>
                  )}
                  {NavbarShoppingCart && (
                    <li>
                      <NavbarShoppingCart />
                    </li>
                  )}
                  <button
                    className="sm:tw-hidden tw-p-3"
                    aria-label={mobileDrawerOpen ? 'close navigation' : 'open navigation'}
                    onClick={() => {
                      setMobileDrawerOpen((currentlyOpen) => !currentlyOpen);
                    }}
                  >
                    {mobileDrawerOpen ? (
                      <CloseIcon className="tw-w-6" />
                    ) : (
                      <HamburgerIcon className="tw-w-6" />
                    )}
                  </button>
                </ul>
              </HeaderPaddingWrapper>
              <MobileNav
                open={mobileDrawerOpen}
                offset={offset}
                navItems={navItems}
                regions={regions}
                searchLink={searchLink}
                account={account}
                dealerLocator={sitewideBanner?.dealerLocatorLink}
                useCustomer={useCustomer}
                useLogout={useLogout}
              />
            </div>
          </NavigationAnimationContext.Provider>
        </header>
      </FocusTrap>
    </>
  );
};

// Export individual components for use in CMS
export { default as NavDropdown } from './_NavDropdown';
export { default as SecondaryNav } from './_SecondaryNav';
export { default as LinkGroup } from './_LinkGroup';
export { default as LinkList } from './_LinkList';
export { default as ProductList } from './_ProductList';

// Export types
export type { NavDropdownProps } from './_NavDropdown';
export type { SecondaryNavProps } from './_SecondaryNav';
export type { LinkGroupProps, LinkWithIconProps } from './_LinkGroup';
export type { LinkListProps } from './_LinkList';

export default Navigation;
